import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ZIFEK — Catégories d'un commerce (marketplace)
//
// Chaque commerce (marketplace) gère SON propre référentiel de
// catégories. L'isolation est stricte : toutes les requêtes sont
// filtrées par `idcommerce = commerceId`. Les catégories globales
// (idcommerce = 0 / null) et celles des autres commerces ne sont
// jamais modifiées ni mélangées ici.
//
// La table `categorie` est partagée (produits, services, annonces) ;
// le champ `type` distingue la nature de chaque catégorie.
// ─────────────────────────────────────────────────────────────

export type CommerceCategoryType = 'produits' | 'services' | 'autres';

export const COMMERCE_CATEGORY_TYPES: { value: CommerceCategoryType; label: string; icon: string }[] = [
  { value: 'produits', label: 'Produits', icon: 'ri-shopping-bag-3-line' },
  { value: 'services', label: 'Services', icon: 'ri-service-line' },
  { value: 'autres', label: 'Annonces', icon: 'ri-megaphone-line' },
];

export interface CommerceCategory {
  id: number;
  nom: string;
  description: string | null;
  type: string | null;
  icon: string | null;
  image_couverture: string | null;
  parent_id: number | null;
  sort_order: number;
  afficher: number;
}

/**
 * Normalise la valeur de `categorie.type` vers l'un des trois buckets de la
 * marketplace. Les catégories créées depuis « dashboard/products » sont
 * taguées « produit » (singulier) : on les ramène à « produits » pour
 * qu'elles soient reconnues automatiquement dans la gestion marketplace.
 */
export function normalizeCommerceCategoryType(type?: string | null): CommerceCategoryType {
  const t = (type || '').trim().toLowerCase();
  if (t.startsWith('produit')) return 'produits';
  if (t.startsWith('service')) return 'services';
  if (t && t !== 'autres') return 'autres';
  return (t as CommerceCategoryType) || 'autres';
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

/** Catégories d'un commerce, triées par ordre d'affichage puis nom. */
export async function fetchCommerceCategories(commerceId: number): Promise<CommerceCategory[]> {
  if (!Number.isFinite(commerceId) || commerceId <= 0) return [];
  try {
    const { data, error } = await supabase
      .from('categorie')
      .select('id, nom, description, type, icon, image_couverture, parent_id, sort_order, afficher, idcommerce')
      .eq('idcommerce', commerceId)
      .order('sort_order', { ascending: true })
      .order('nom', { ascending: true });
    if (error || !data) return [];
    return (data as Record<string, unknown>[]).map((c) => ({
      id: Number(c.id),
      nom: (c.nom as string) || '',
      description: (c.description as string) || null,
      type: normalizeCommerceCategoryType(c.type as string | null),
      icon: (c.icon as string) || null,
      image_couverture: (c.image_couverture as string) || null,
      // Les catégories racines peuvent avoir parent_id = 0 (dashboard/products)
      // OU null (gestion marketplace) : on normalise à null pour un repérage fiable.
      parent_id: c.parent_id == null || Number(c.parent_id) === 0 ? null : Number(c.parent_id),
      sort_order: Number(c.sort_order || 0),
      afficher: c.afficher == null ? 1 : Number(c.afficher),
    }));
  } catch {
    return [];
  }
}

export interface CommerceCategoryInput {
  commerceId: number;
  nom: string;
  type: CommerceCategoryType;
  description?: string | null;
  icon?: string | null;
  image_couverture?: string | null;
  parentId?: number | null;
}

/** Crée une catégorie rattachée au commerce (placée en fin de liste). */
export async function createCommerceCategory(input: CommerceCategoryInput): Promise<number> {
  const nom = input.nom.trim();
  if (!nom) throw new Error('Le nom de la catégorie est obligatoire.');

  // Position : après le dernier sort_order du commerce.
  let nextOrder = 0;
  try {
    const { data } = await supabase
      .from('categorie')
      .select('sort_order')
      .eq('idcommerce', input.commerceId)
      .order('sort_order', { ascending: false })
      .limit(1)
      .maybeSingle();
    nextOrder = Number((data as { sort_order?: number } | null)?.sort_order ?? -1) + 1;
  } catch {
    nextOrder = 0;
  }

  const { data, error } = await supabase
    .from('categorie')
    .insert({
      nom,
      description: input.description?.trim() || null,
      idcommerce: input.commerceId,
      afficher: 1,
      type: input.type,
      icon: input.icon || null,
      image_couverture: input.image_couverture || null,
      parent_id: input.parentId ?? null,
      sort_order: nextOrder,
      slug: slugify(nom),
      status: 'active',
    })
    .select('id')
    .single();
  if (error) throw error;
  return Number((data as { id: number }).id);
}

export interface CommerceCategoryPatch {
  nom?: string;
  type?: CommerceCategoryType;
  description?: string | null;
  icon?: string | null;
  image_couverture?: string | null;
  parentId?: number | null;
}

/** Met à jour une catégorie (isolée au commerce). */
export async function updateCommerceCategory(
  id: number,
  commerceId: number,
  patch: CommerceCategoryPatch,
): Promise<void> {
  const row: Record<string, unknown> = {};
  if (patch.nom !== undefined) {
    const nom = patch.nom.trim();
    if (!nom) throw new Error('Le nom de la catégorie est obligatoire.');
    row.nom = nom;
    row.slug = slugify(nom);
  }
  if (patch.type !== undefined) row.type = patch.type;
  if (patch.description !== undefined) row.description = patch.description?.trim() || null;
  if (patch.icon !== undefined) row.icon = patch.icon || null;
  if (patch.image_couverture !== undefined) row.image_couverture = patch.image_couverture || null;
  if (patch.parentId !== undefined) row.parent_id = patch.parentId ?? null;
  if (Object.keys(row).length === 0) return;
  const { error } = await supabase
    .from('categorie')
    .update(row)
    .eq('id', id)
    .eq('idcommerce', commerceId);
  if (error) throw error;
}

/**
 * Migration des vendeurs quand une catégorie qui était « principale »
 * (racine) devient une SOUS-catégorie d'une autre catégorie.
 *
 * Tous les vendeurs qui avaient cette catégorie comme catégorie principale
 * héritent du changement : leur nouvelle catégorie principale devient le
 * parent, et l'ancienne catégorie principale devient leur sous-catégorie.
 *
 * L'opération est idempotente : après migration, plus aucun vendeur ne
 * pointe sur `categoryId` comme catégorie principale.
 *
 * @returns le nombre de vendeurs migrés.
 */
export async function migrateVendorsOnReparent(
  commerceId: number,
  categoryId: number,
  newParentId: number,
): Promise<number> {
  if (!commerceId || !categoryId || !newParentId || categoryId === newParentId) return 0;
  try {
    const { data: cats } = await supabase
      .from('categorie')
      .select('id, nom')
      .in('id', [categoryId, newParentId]);
    const rows = (cats as { id: number; nom: string }[] | null) || [];
    const nameOf = (id: number) => rows.find((c) => Number(c.id) === id)?.nom || null;
    const parentName = nameOf(newParentId);
    const childName = nameOf(categoryId);
    const { data: updated, error } = await supabase
      .from('userwebsite')
      .update({
        categorie_id: newParentId,
        categorie_nom: parentName,
        souscategorie_id: categoryId,
        souscategorie_nom: childName,
      })
      .eq('idcommerce', commerceId)
      .eq('categorie_id', categoryId)
      .select('id');
    if (error) return 0;
    return (updated as { id: number }[] | null)?.length || 0;
  } catch {
    return 0;
  }
}

/** Affiche ou masque une catégorie sur le site public. */
export async function setCommerceCategoryVisible(
  id: number,
  commerceId: number,
  visible: boolean,
): Promise<void> {
  const { error } = await supabase
    .from('categorie')
    .update({ afficher: visible ? 1 : 0 })
    .eq('id', id)
    .eq('idcommerce', commerceId);
  if (error) throw error;
}

/** Supprime une catégorie du commerce. */
export async function deleteCommerceCategory(id: number, commerceId: number): Promise<void> {
  const { error } = await supabase
    .from('categorie')
    .delete()
    .eq('id', id)
    .eq('idcommerce', commerceId);
  if (error) throw error;
}

/** Applique un nouvel ordre d'affichage à une liste de catégories du commerce. */
export async function reorderCommerceCategories(
  commerceId: number,
  orderedIds: number[],
): Promise<void> {
  const ids = orderedIds.filter((n) => Number.isFinite(n));
  await Promise.all(
    ids.map((id, index) =>
      supabase
        .from('categorie')
        .update({ sort_order: index })
        .eq('id', id)
        .eq('idcommerce', commerceId),
    ),
  );
}

// ── Compteur de vendeurs par catégorie / sous-catégorie ────────

export interface CategoryVendorCounts {
  /** Nombre de vendeurs ayant déclaré la catégorie comme catégorie PRINCIPALE. */
  byCategory: Record<number, number>;
  /** Nombre de vendeurs ayant déclaré la catégorie comme SOUS-catégorie. */
  bySubcategory: Record<number, number>;
}

/**
 * Compteur de vendeurs par catégorie / sous-catégorie d'un commerce.
 * Le propriétaire de la marketplace repère ainsi les catégories qui
 * concentrent le plus de vendeurs (celles à réorganiser en priorité).
 */
export async function fetchCommerceCategoryVendorCounts(
  commerceId: number,
): Promise<CategoryVendorCounts> {
  const empty: CategoryVendorCounts = { byCategory: {}, bySubcategory: {} };
  if (!Number.isFinite(commerceId) || commerceId <= 0) return empty;
  try {
    const { data, error } = await supabase
      .from('userwebsite')
      .select('categorie_id, souscategorie_id')
      .eq('idcommerce', commerceId);
    if (error || !data) return empty;
    const byCategory: Record<number, number> = {};
    const bySubcategory: Record<number, number> = {};
    (data as { categorie_id: number | null; souscategorie_id: number | null }[]).forEach((r) => {
      const main = Number(r.categorie_id);
      if (Number.isFinite(main) && main > 0) byCategory[main] = (byCategory[main] || 0) + 1;
      const sub = Number(r.souscategorie_id);
      if (Number.isFinite(sub) && sub > 0) bySubcategory[sub] = (bySubcategory[sub] || 0) + 1;
    });
    return { byCategory, bySubcategory };
  } catch {
    return empty;
  }
}